import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

/**
 * QuestionBlock Component
 * Renders embedded questions in documents (MCQ, short answer, etc.)
 *
 * Supports:
 * - Multiple choice (single/multiple answer)
 * - Short answer
 * - Code questions
 */
const QuestionBlock = ({
  questionData,
  isStudent = false,
  onSubmitAnswer,
  studentAnswer = null,
  showFeedback = false,
  readOnly = false
}) => {
  const [selectedOptions, setSelectedOptions] = useState(studentAnswer?.selectedOptions || []);
  const [shortAnswer, setShortAnswer] = useState(studentAnswer?.shortAnswer || '');
  const [submitted, setSubmitted] = useState(!!studentAnswer || readOnly);

  const handleOptionToggle = (option) => {
    if (questionData.type === 'single') {
      setSelectedOptions([option]);
    } else {
      setSelectedOptions(prev =>
        prev.includes(option)
          ? prev.filter(o => o !== option)
          : [...prev, option]
      );
    }
  };

  const handleSubmit = () => {
    const answer = questionData.questionType === 'mcq'
      ? { selectedOptions }
      : { shortAnswer };

    setSubmitted(true);
    if (onSubmitAnswer) {
      onSubmitAnswer(questionData.id, answer);
    }
  };

  const checkAnswer = () => {
    if (questionData.questionType === 'mcq') {
      const rubric = Array.isArray(questionData.rubric)
        ? questionData.rubric
        : [questionData.rubric];

      const correctOptions = rubric.sort().join(',');
      const selectedSorted = selectedOptions.sort().join(',');

      return correctOptions === selectedSorted;
    }
    return null; // Short answer needs manual grading
  };

  const isCorrect = submitted ? checkAnswer() : null;

  // Render MCQ
  if (questionData.questionType === 'mcq') {
    return (
      <div className="my-6 border-2 border-blue-300 rounded-lg overflow-hidden bg-blue-50/30">
        {/* Header */}
        <div className="bg-blue-100 border-b border-blue-300 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-semibold text-blue-900">
              Question {questionData.id}
            </span>
            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
              {questionData.type === 'single' ? 'Single Choice' : 'Multiple Choice'}
            </span>
          </div>
          {questionData.lo && (
            <span className="text-xs text-blue-700 italic">
              LO: {questionData.lo}
            </span>
          )}
        </div>

        {/* Question Body */}
        <div className="p-4">
          <div className="mb-4 text-gray-900 font-medium">
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex]}
            >
              {questionData.question}
            </ReactMarkdown>
          </div>

          {/* Options */}
          <div className="space-y-2">
            {questionData.options.map((option) => {
              const isSelected = selectedOptions.includes(option.key);
              const isCorrectOption = questionData.rubric.includes(option.key);
              const showCorrect = submitted && showFeedback && isCorrectOption;
              const showIncorrect = submitted && showFeedback && isSelected && !isCorrectOption;

              return (
                <button
                  key={option.key}
                  onClick={() => !submitted && handleOptionToggle(option.key)}
                  disabled={submitted}
                  className={`w-full text-left p-3 border-2 rounded-lg transition-all duration-200 ${
                    showCorrect
                      ? 'border-green-500 bg-green-50'
                      : showIncorrect
                      ? 'border-red-500 bg-red-50'
                      : isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/50'
                  } ${submitted ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                      showCorrect
                        ? 'border-green-500 bg-green-500'
                        : showIncorrect
                        ? 'border-red-500 bg-red-500'
                        : isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-400'
                    }`}>
                      {isSelected && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                      {showCorrect && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {showIncorrect && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <span className={`font-semibold mr-2 ${
                        showCorrect ? 'text-green-900' : showIncorrect ? 'text-red-900' : 'text-gray-700'
                      }`}>
                        {option.key}:
                      </span>
                      <span className={
                        showCorrect ? 'text-green-800' : showIncorrect ? 'text-red-800' : 'text-gray-800'
                      }>
                        {option.text}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Submit Button */}
          {!submitted && (
            <button
              onClick={handleSubmit}
              disabled={selectedOptions.length === 0}
              className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
            >
              Check Answer
            </button>
          )}

          {/* Feedback */}
          {submitted && (
            <div className={`mt-4 p-4 rounded-lg ${
              isCorrect ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
            }`}>
              <div className="flex items-center space-x-2 mb-2">
                {isCorrect ? (
                  <>
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold text-green-900">Correct!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold text-red-900">Incorrect</span>
                  </>
                )}
              </div>
              {questionData.explanation && (
                <div className={`text-sm mt-2 ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {questionData.explanation}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render Short Answer
  if (questionData.questionType === 'short') {
    return (
      <div className="my-6 border-2 border-purple-300 rounded-lg overflow-hidden bg-purple-50/30">
        {/* Header */}
        <div className="bg-purple-100 border-b border-purple-300 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-semibold text-purple-900">
              Question {questionData.id}
            </span>
            <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded">
              Short Answer
            </span>
          </div>
          {questionData.lo && (
            <span className="text-xs text-purple-700 italic">
              LO: {questionData.lo}
            </span>
          )}
        </div>

        {/* Question Body */}
        <div className="p-4">
          <div className="mb-4 text-gray-900 font-medium">
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex]}
            >
              {questionData.question}
            </ReactMarkdown>
          </div>

          {/* Answer Box */}
          <textarea
            value={shortAnswer}
            onChange={(e) => setShortAnswer(e.target.value)}
            disabled={submitted}
            placeholder="Type your answer here..."
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            rows={4}
          />

          {/* Submit Button */}
          {!submitted && (
            <button
              onClick={handleSubmit}
              disabled={!shortAnswer.trim()}
              className="mt-4 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
            >
              Submit Answer
            </button>
          )}

          {/* Show rubric for instructors always, for students after submission */}
          {(!isStudent || submitted) && questionData.rubric && (
            <div className="mt-4 p-4 bg-purple-100 border border-purple-300 rounded-lg">
              <h4 className="text-sm font-semibold text-purple-900 mb-2">Expected Answer:</h4>
              <div className="text-sm text-purple-800">
                <ReactMarkdown
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                >
                  {questionData.rubric}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default QuestionBlock;
